import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AppointmentTask,
  AppointmentTaskStatus,
  FOLLOW_UP_APPOINTMENT_TASK_ID,
} from "../../constants/AppointmentOverlayTasks";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
  VisitActivity,
} from "../../constants/AppointmentActivity";

/** How the clinician reaches the patient for this visit. */
export interface AppointmentConnectivity {
  /** Appointment mode name as the API gives it, e.g. "Video Call" or "Telephone". */
  mode: string;
  /**
   * Video platform for a video visit. Zoom resolves a host URL at click time. The
   * patient-facing `webinarUrl` is deliberately not carried here -- this widget is the
   * clinician's way in.
   */
  videoProvider: string | null;
  /** Preferred contact number, for a phone visit. */
  phone: string | null;
}

export interface AppointmentOverlayState {
  /** Null means no appointment is in progress -- the overlay renders nothing. */
  appointmentId: string | null;
  patientId: string | null;
  patientName: string;
  /** ISO timestamp, used to drop stale state when rehydrating after a refresh. */
  startedAt: string | null;
  /** The appointment's booked length in minutes, as scheduled. */
  scheduledMinutes: number | null;
  /** Route for the "Appointment details" button, e.g. /schedule/charting/:id */
  detailsPath: string | null;
  collapsed: boolean;
  connectivity: AppointmentConnectivity | null;
  tasks: AppointmentTask[];
  /**
   * The focus the clinician chose for this visit (a journey step, or the Care
   * Management note); null follows the roadmap's suggestion. Kept here so it
   * survives a page refresh.
   */
  journeyStepId: string | null;
  /**
   * How many checklist rows were showing once loaded, so after a refresh the
   * loading placeholders match. Null until the checklist first loads.
   */
  checklistRowCount: number | null;
  patientVerified: boolean;
  visitActivities: VisitActivity[];
}

export interface StartAppointmentPayload {
  appointmentId: string;
  patientId: string;
  patientName: string;
  detailsPath: string;
  scheduledMinutes: number | null;
  connectivity: AppointmentConnectivity | null;
  tasks: AppointmentTask[];
}

const initialState: AppointmentOverlayState = {
  appointmentId: null,
  patientId: null,
  patientName: "",
  startedAt: null,
  scheduledMinutes: null,
  detailsPath: null,
  collapsed: false,
  connectivity: null,
  tasks: [],
  journeyStepId: null,
  checklistRowCount: null,
  patientVerified: false,
  visitActivities: [],
};

/** Booking an appointment for the patient during the visit is the follow-up. */
const syncFollowUpTask = (state: AppointmentOverlayState) => {
  const booked = state.visitActivities.some(
    (activity) =>
      activity.activityType === AppointmentActivityType.APPOINTMENT &&
      activity.status === AppointmentActivityAction.COMPLETED
  );
  const task = state.tasks.find((t) => t.id === FOLLOW_UP_APPOINTMENT_TASK_ID);
  if (task && booked) {
    task.status = AppointmentTaskStatus.COMPLETED;
  }
};

const appointmentOverlaySlice = createSlice({
  name: "appointmentOverlay",
  initialState,
  reducers: {
    startAppointment: (
      state,
      action: PayloadAction<StartAppointmentPayload>
    ) => {
      state.appointmentId = action.payload.appointmentId;
      state.patientId = action.payload.patientId;
      state.patientName = action.payload.patientName;
      state.detailsPath = action.payload.detailsPath;
      state.scheduledMinutes = action.payload.scheduledMinutes;
      state.connectivity = action.payload.connectivity;
      state.tasks = action.payload.tasks;
      state.startedAt = new Date().toISOString();
      state.collapsed = false;
      state.journeyStepId = null;
      state.checklistRowCount = null;
      state.patientVerified = false;
      state.visitActivities = [];
    },
    finishAppointment: () => initialState,
    overlayStateSynced: (_state, action: PayloadAction<AppointmentOverlayState | null>) =>
      action.payload ?? initialState,
    toggleCollapsed: (state) => {
      state.collapsed = !state.collapsed;
    },
    setJourneyStepId: (state, action: PayloadAction<string | null>) => {
      state.journeyStepId = action.payload;
    },
    setChecklistRowCount: (state, action: PayloadAction<number>) => {
      state.checklistRowCount = action.payload;
    },
    verifyPatient: (state) => {
      state.patientVerified = true;
    },
    /** Replaces the visit's activities with what the API has, e.g. after a refresh. */
    visitActivitiesLoaded: (state, action: PayloadAction<VisitActivity[]>) => {
      state.visitActivities = action.payload;
      syncFollowUpTask(state);
    },
    /** Adds or updates one activity as soon as it's logged. */
    visitActivityLogged: (state, action: PayloadAction<VisitActivity>) => {
      const index = state.visitActivities.findIndex(
        (activity) =>
          activity.activityType === action.payload.activityType &&
          activity.refId === action.payload.refId
      );
      if (index === -1) {
        state.visitActivities.push(action.payload);
      } else {
        state.visitActivities[index] = action.payload;
      }
      syncFollowUpTask(state);
    },
  },
});

export const {
  startAppointment,
  finishAppointment,
  overlayStateSynced,
  toggleCollapsed,
  setJourneyStepId,
  setChecklistRowCount,
  verifyPatient,
  visitActivitiesLoaded,
  visitActivityLogged,
} = appointmentOverlaySlice.actions;

export { initialState as appointmentOverlayInitialState };
export default appointmentOverlaySlice.reducer;
